import React, { Component } from 'react';
import styled from 'styled-components';
import {
    DropdownContainer,
    Dropdown,
} from '@zocdoc-frontend-common/components-dropdown';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import { Column, Row } from '@zocdoc-frontend-common/styles-grid';
import Textfield from '@zocdoc-mezzanine/components-textfield';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import { labelSmall } from '@zocdoc-mezzanine/foundations-typography';
import { states } from './states';

/**
 * @param {function(address1: string): void} changeAddress1
 * @param {function(address2: string): void} changeAddress2
 * @param {function(city: string): void} changeCity
 * @param {function(state: string): void} changeState
 * @param {function(zip: string): void} changeZip
 * @returns {JSX.Element}
 * @constructor
 */
export class AddressFormView extends Component {
    componentDidMount() {
        this.truncateZip();
    }

    componentDidUpdate(prevProps) {
        if (this.props.zip !== prevProps.zip) {
            this.truncateZip();
        }
    }

    /*
        Chrome autofill doesn't respect HTML maxlength attribute
        and can fill the input with trailing whitespace. Handle that
        edge case by still truncating down to 5 on mount or whenever
        else we somehow receive an invalidly long value.
    */
    truncateZip() {
        const { changeZip, zip } = this.props;
        if (zip && zip.length > 5) {
            changeZip(zip.slice(0, 5));
        }
    }

    render() {
        const {
            changeAddress1,
            changeAddress2,
            changeCity,
            changeState,
            changeZip,
            address1,
            address2,
            city,
            zip,
            selectedState,
            trackSelectAddress1,
            trackSelectAddress2,
            trackSelectCity,
            trackSelectState,
            trackSelectZip,
            errors,
        } = this.props;

        return (
            <Row>
                <FormRow className="exclude-from-playback">
                    <RequirementLabel id="street-address-label">
                        Street address
                    </RequirementLabel>
                    <Textfield
                        aria-labelledby="street-address-label"
                        data-test="address-1"
                        invalidMessage={processDataForMessage(
                            errors.address_line_1
                        )}
                        onChange={(e) => changeAddress1(e.target.value)}
                        onFocus={trackSelectAddress1}
                        value={address1}
                    />
                </FormRow>
                <FormRow className="exclude-from-playback">
                    <RequirementLabel id="apt-suite-label">
                        Apartment/suite #
                    </RequirementLabel>
                    <Textfield
                        aria-labelledby="apt-suite-label"
                        data-test="address-2"
                        invalidMessage={processDataForMessage(
                            errors.address_line_2
                        )}
                        onChange={(e) => changeAddress2(e.target.value)}
                        onFocus={trackSelectAddress2}
                        value={address2}
                    />
                </FormRow>
                <FormRow className="exclude-from-playback">
                    <RequirementLabel id="city-label">City</RequirementLabel>
                    <Textfield
                        aria-labelledby="city-label"
                        data-test="city"
                        invalidMessage={processDataForMessage(errors.city)}
                        onChange={(e) => changeCity(e.target.value)}
                        onFocus={trackSelectCity}
                        value={city}
                    />
                </FormRow>
                <FormRow>
                    <Column size={12}>
                        <StateWrapper>
                            <RequirementLabel id="state-label">
                                State
                            </RequirementLabel>
                            <div
                                className="exclude-from-playback"
                                data-test="exclude-state-from-playback"
                            >
                                <DropdownContainer
                                    data-test="state-container"
                                    onChange={(value) => {
                                        changeState(value);
                                        trackSelectState();
                                    }}
                                    selected={selectedState}
                                >
                                    <Dropdown
                                        aria-labelledby="state-label"
                                        data-test="state"
                                        invalidMessage={processDataForMessage(
                                            errors.state
                                        )}
                                        options={states}
                                    />
                                </DropdownContainer>
                            </div>
                        </StateWrapper>
                    </Column>
                    <Column size={12}>
                        <ZipWrapper className="exclude-from-playback">
                            <RequirementLabel id="zip-code-label">
                                Zip
                            </RequirementLabel>
                            <Textfield
                                aria-labelledby="zip-code-label"
                                data-test="zip"
                                invalidMessage={processDataForMessage(
                                    errors.zip_code
                                )}
                                maxLength={5}
                                onChange={(e) => changeZip(e.target.value)}
                                onFocus={trackSelectZip}
                                value={zip}
                            />
                        </ZipWrapper>
                    </Column>
                </FormRow>
            </Row>
        );
    }
}

const RequirementLabel = styled.label`
    ${labelSmall}
    color: ${blue100};
`;

const StateWrapper = styled.div`
    padding-right: 10px;

    select:focus + div {
        border-color: ${blue100};
    }
`;

const ZipWrapper = styled.div`
    padding-left: 10px;
`;

function processDataForMessage(message) {
    if (message && message.defaultMessage) {
        return message.defaultMessage;
    }

    return message;
}
